// Inspecciones ("/inspecciones") — standalone staff-reachable entry point to
// the DVI (Digital Vehicle Inspection) feature. DVI is otherwise only
// reachable from the owner/manager-only Service cockpit (Service.tsx's Dvi
// tab); staff technicians hold the dvi:view/dvi:write permissions by role
// default but not view:service, so they had no door in. Standalone
// top-level page (not nested under a layout's <Outlet>) — mirrors
// Pacesetter.tsx/Reception.tsx: fetch the user itself, render its own
// Header, self-gate via canViewDvi (same helper Header.tsx uses to decide
// whether to show the nav link at all).
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Button, Card, Title } from '@tremor/react';
import { currentUser } from '../../api/auth';
import { fetchDviRecent } from '../../api/dvi';
import AccessDenied from '../../components/AccessDenied';
import Header from '../../components/Header';
import DviRecentList from './DviRecentList';
import { canViewDvi } from '../../lib/roles';
import { t } from '../../lib/i18n';
import type { DviInspection, User } from '../../api/types';

export default function Inspecciones() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);
  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        {canViewDvi(user) ? <InspeccionesInner /> : <AccessDenied user={user} />}
      </main>
    </div>
  );
}

function InspeccionesInner() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<DviInspection[] | null>(null);
  // Kept as raw strings (not numbers) so an empty field reads as '' rather
  // than coercing to 0 — Number('') is 0 (finite!), so the emptiness check
  // below has to happen on the string, not the parsed number.
  const [sidInput, setSidInput] = useState('');
  const [tnumInput, setTnumInput] = useState('');
  const c = t.dvi;

  useEffect(() => {
    let alive = true;
    // All stores (sid=null) — this is the staff-facing "look up any WO"
    // entry point, not scoped to a single store's cockpit session.
    fetchDviRecent(null)
      .then((r) => { if (alive) setRows(r); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, []);

  const sidNum = Number(sidInput);
  const tnumNum = Number(tnumInput);
  const valid =
    sidInput.trim() !== '' && tnumInput.trim() !== '' &&
    Number.isFinite(sidNum) && Number.isFinite(tnumNum) &&
    sidNum > 0 && tnumNum > 0;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // Re-checked here (not just via the submit button's `disabled`) so a
    // stray Enter-to-submit can't slip a NaN/empty pair through.
    if (!valid) return;
    navigate(`/dvi/captura/${sidNum}/${tnumNum}`);
  }

  return (
    <div className="space-y-4">
      <Title>{c.nav}</Title>

      <Card>
        <Title className="text-base">{c.startByWo}</Title>
        <form onSubmit={handleSubmit} className="mt-3 flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <div className="text-tremor-content text-xs mb-1">{c.store}</div>
            <input
              type="number"
              min="1"
              value={sidInput}
              onChange={(e) => setSidInput(e.target.value)}
              className="w-28 rounded border border-tremor-border px-3 py-2 bg-tremor-background"
            />
          </label>
          <label className="text-sm">
            <div className="text-tremor-content text-xs mb-1">{c.woNumber}</div>
            <input
              type="number"
              min="1"
              value={tnumInput}
              onChange={(e) => setTnumInput(e.target.value)}
              className="w-28 rounded border border-tremor-border px-3 py-2 bg-tremor-background"
            />
          </label>
          <Button type="submit" disabled={!valid}>{c.start}</Button>
        </form>
      </Card>

      <Card>
        <Title className="text-base mb-3">{c.title}</Title>
        <DviRecentList rows={rows} />
      </Card>
    </div>
  );
}
